// src/utils/chunkLoadRecovery.js
//
// A tab that outlives a hosting deploy asks for its own build's page chunks, which the deploy removed
// (docs/bugs/stale_tab_missing_chunk_BUG.md). The fix is the new build: load the page asked for again,
// once. This file is the pure policy; src/services/backgroundRefresh.js (recoverFromMissingChunk) is the
// browser wiring, so the same blockers that hold a cache-version refresh (a live call, a walkie call, a
// record under edit, an upload) hold this one too. scripts/verify-chunk-load-recovery.js tables it.

export const CHUNK_RELOAD_KEY = 'gse_chunk_reload'
// A second failure for the same page inside this window means the reload did not help (the new build
// is missing the chunk too, or the network is down): stop and say so instead of looping.
export const CHUNK_RELOAD_WINDOW_MS = 60 * 1000

// webpack 5: `ChunkLoadError` ("Loading chunk 8996 failed."); mini-css-extract: "Loading CSS chunk 8996
// failed."; native dynamic import (Chromium / Safari) for completeness.
const CHUNK_ERROR_MESSAGES = [
  /Loading (CSS )?chunk [\w-]+ failed/i,
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
]

export function isChunkLoadError(error) {
  if (!error) return false
  if (error.name === 'ChunkLoadError' || error.code === 'CSS_CHUNK_LOAD_FAILED') return true
  const message = typeof error === 'string' ? error : error.message
  return typeof message === 'string' && CHUNK_ERROR_MESSAGES.some(pattern => pattern.test(message))
}

export function readChunkReloadAttempt(storage) {
  try {
    const parsed = JSON.parse(storage?.getItem(CHUNK_RELOAD_KEY) || 'null')
    if (!parsed || typeof parsed.target !== 'string' || !Number.isFinite(parsed.at)) return null
    return { target: parsed.target, at: parsed.at }
  } catch {
    return null
  }
}

export function writeChunkReloadAttempt(storage, { target, at }) {
  try {
    storage?.setItem(CHUNK_RELOAD_KEY, JSON.stringify({ target, at }))
    return true
  } catch {
    return false
  }
}

/**
 * What to do about a missing chunk.
 *   reload   load the new build at `target`
 *   blocked  a blocker says "not now"; tell the user to refresh when they are done
 *   offline  the browser is offline: the chunk may well exist, and a reload would trade the page for the
 *            browser's own "no internet" page; tell the user to try again once connected
 *   gave-up  this page already reloaded for the same reason moments ago, or the attempt could not be
 *            recorded (no storage = no loop guard, so no automatic reload); tell the user to refresh
 */
export function decideChunkRecovery({
  target,
  blockedBy = [],
  lastAttempt = null,
  canRecord = true,
  online = true,
  now = Date.now(),
}) {
  if (!online) return { action: 'offline' }
  if (blockedBy.length > 0) return { action: 'blocked', blockedBy }
  if (lastAttempt && lastAttempt.target === target && now - lastAttempt.at < CHUNK_RELOAD_WINDOW_MS) {
    return { action: 'gave-up', reason: 'reloaded-recently' }
  }
  if (!canRecord) return { action: 'gave-up', reason: 'no-storage' }
  return { action: 'reload', target }
}
